import { useMemo, useRef, useCallback, useEffect } from "react";
import type { Subtask, TaskId } from "../../../../shared/types";

type TaskUpdate = Partial<{
  title: string;
  priority: "urgent" | "normal" | "low";
  notes: string;
  tags: string[];
  dueDate: string;
  subtasks: readonly Subtask[];
  category: string;
}>;

/**
 * Hook pour debouncer les mises à jour de tâche.
 * Accumule les changements et envoie une seule requête après un délai d'inactivité.
 */
export function useDebouncedTaskUpdate(
  taskId: TaskId,
  onUpdate: (id: TaskId, updates: TaskUpdate) => void,
  delay = 600,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingUpdatesRef = useRef<TaskUpdate>({});

  // Fonction pour envoyer les changements accumulés
  const sendUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;
    if (Object.keys(updates).length > 0) {
      onUpdate(taskId, updates);
      pendingUpdatesRef.current = {};
    }
  }, [taskId, onUpdate]);

  // Flush au démontage du composant
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      sendUpdates();
    };
  }, [sendUpdates]);

  // Fonction de mise à jour debouncée
  const debouncedUpdate = useCallback(
    (updates: TaskUpdate) => {
      // Accumuler les changements
      pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates };

      // Annuler le timeout précédent
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Créer un nouveau timeout
      timeoutRef.current = setTimeout(() => {
        sendUpdates();
        timeoutRef.current = undefined;
      }, delay);
    },
    [delay, sendUpdates],
  );

  // Fonction pour forcer l'envoi immédiat (pour les actions critiques)
  const immediateUpdate = useCallback(
    (updates: TaskUpdate) => {
      // Annuler le timeout en cours
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      // Envoyer les changements en attente + la nouvelle mise à jour
      const pending = pendingUpdatesRef.current;
      onUpdate(taskId, { ...pending, ...updates });
      pendingUpdatesRef.current = {};
    },
    [taskId, onUpdate],
  );

  // Fonction pour flusher manuellement
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    sendUpdates();
  }, [sendUpdates]);

  return useMemo(
    () => ({
      update: debouncedUpdate,
      updateImmediate: immediateUpdate,
      flush,
    }),
    [debouncedUpdate, immediateUpdate, flush],
  );
}
